import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useAlert } from "pages/context/AlertContext";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("pt-PT");
};

const fmtDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString("pt-PT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0,00";
};

function AgruparLinhasPorFatura({ faturas = [], linhas = [] }) {
  const linhasPorFatura = useMemo(() => {
    const map = new Map();

    (Array.isArray(linhas) ? linhas : []).forEach((ln) => {
      const key = String(ln?.faturanumero || "");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ln);
    });

    return map;
  }, [linhas]);

  if (!Array.isArray(faturas) || faturas.length === 0) {
    return (
      <MDTypography variant="caption" color="text">
        Não existem faturas para apresentar.
      </MDTypography>
    );
  }

  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      {faturas.map((fat, idx) => {
        const key = String(fat?.faturanumero || "");
        const linhasFat = linhasPorFatura.get(key) || [];

        return (
          <Paper key={`${key}_${idx}`} variant="outlined" sx={{ p: 1.25 }}>
            <MDBox mb={0.75}>
              <MDTypography variant="button" fontWeight="bold">
                Fatura: {fat?.faturanumero || "-"}
              </MDTypography>

              <MDTypography variant="caption" display="block">
                Data: {fmtDate(fat?.faturadata)}
              </MDTypography>

              <MDTypography variant="caption" display="block">
                DocRef: {fat?.docref || "-"}
              </MDTypography>
            </MDBox>

            {!linhasFat.length ? (
              <MDTypography variant="caption" color="text">
                Sem linhas para esta fatura.
              </MDTypography>
            ) : (
              <MDBox sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "6px 4px" }}>Ref.</th>
                      <th style={{ textAlign: "left", padding: "6px 4px" }}>Artigo</th>
                      <th style={{ textAlign: "right", padding: "6px 4px" }}>Qtd.</th>
                      <th style={{ textAlign: "right", padding: "6px 4px" }}>Preço</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasFat.map((ln, i) => (
                      <tr key={`${key}_ln_${i}`}>
                        <td style={{ padding: "6px 4px", borderTop: "1px solid #eee" }}>
                          {ln?.refmaterial || ln?.catitemref || "-"}
                        </td>
                        <td style={{ padding: "6px 4px", borderTop: "1px solid #eee" }}>
                          {ln?.catitemnome || "-"}
                        </td>
                        <td
                          style={{
                            padding: "6px 4px",
                            borderTop: "1px solid #eee",
                            textAlign: "right",
                          }}
                        >
                          {ln?.qtd ?? "-"}
                        </td>
                        <td
                          style={{
                            padding: "6px 4px",
                            borderTop: "1px solid #eee",
                            textAlign: "right",
                          }}
                        >
                          {fmtMoney(ln?.precoliquido)} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MDBox>
            )}
          </Paper>
        );
      })}
    </MDBox>
  );
}

export default function DialogClienteDebitoInfo({ open, onClose, item, onOpenContactos }) {
  const { temFuncao, userData } = useAuth();
  const { showAlert } = useAlert();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const refVendedorAtual =
    !isAdminLike && temFuncao("Comercial") ? userData?.refvendedor || "" : "";

  const [loading, setLoading] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [openInativarAviso, setOpenInativarAviso] = useState(false);
  const [savingInativarAviso, setSavingInativarAviso] = useState(false);

  useEffect(() => {
    let active = true;

    const carregar = async () => {
      if (!open || !item?.refcliente) return;

      setLoading(true);

      const body = {
        refcliente: item?.refcliente || "",
        refpagador: item?.refpagador || "",
        refvendedor: item?.refvendedor || "",
      };

      const [payload, err] = await handleApi(
        api.post("Comercial/SelClienteContactosDetalhe", body)
      );

      if (!active) return;

      setLoading(false);

      if (err) {
        setHistorico([]);
        setFaturas([]);
        setLinhas([]);
        return;
      }

      const data = payload || {};
      setHistorico(Array.isArray(data?.historico) ? data.historico : []);
      setFaturas(Array.isArray(data?.faturas) ? data.faturas : []);
      setLinhas(Array.isArray(data?.linhas) ? data.linhas : []);
    };

    carregar();

    return () => {
      active = false;
    };
  }, [open, item, isAdminLike, refVendedorAtual]);

  const handleConfirmarInativarAviso = async () => {
    const idUtilizador = Number(userData?.id || 0);

    if (!isAdminLike) {
      showAlert("Não tem permissão para inativar este aviso.", "error");
      return;
    }

    if (!item?.refcliente || !item?.refvendedor) {
      showAlert("Dados do cliente inválidos.", "error");
      return;
    }

    if (idUtilizador <= 0) {
      showAlert("Não foi possível identificar o utilizador autenticado.", "error");
      return;
    }

    setSavingInativarAviso(true);

    const body = {
      refcliente: item?.refcliente || "",
      refpagador: item?.refpagador || "",
      refvendedor: item?.refvendedor || "",
      idutilizador: idUtilizador,
      observacoes: "",
    };

    const [payload, err] = await handleApi(
      api.post("Comercial/InsClienteDebitoAlertaInativo", body)
    );

    setSavingInativarAviso(false);

    if (err) {
      showAlert(err?.message || "Erro ao inativar o aviso.", "error");
      return;
    }

    showAlert(payload?.message || "Aviso inativado com sucesso.", "success");
    setOpenInativarAviso(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <MDBox>
            <MDTypography variant="h6">{item?.cliente || "-"}</MDTypography>

            <MDTypography variant="button" color="text" display="block">
              Pagador: {item?.pagador || "-"}
            </MDTypography>

            <MDTypography variant="caption" color="text" display="block" sx={{ mt: 0.5 }}>
              Vendedor: {item?.vendedor || "-"}
            </MDTypography>

            <MDTypography variant="caption" color="text" display="block">
              Última venda: {fmtDate(item?.data_ultima_venda)}
            </MDTypography>

            <MDTypography variant="caption" color="text" display="block">
              Débito: {fmtMoney(item?.valor_debito)} €
            </MDTypography>

            <MDTypography variant="caption" color="text" display="block">
              Débito +90d: {fmtMoney(item?.valor_debito_90mais)} €
            </MDTypography>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={1}>
            <Tooltip title="Gerir contactos">
              <span>
                <IconButton color="info" onClick={() => onOpenContactos?.(item)}>
                  <Icon>calendar_month</Icon>
                </IconButton>
              </span>
            </Tooltip>

            {isAdminLike ? (
              <Tooltip title="Inativar este aviso">
                <span>
                  <IconButton color="error" onClick={() => setOpenInativarAviso(true)}>
                    <Icon>notifications_off</Icon>
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </MDBox>
        </MDBox>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <MDBox py={4} display="flex" justifyContent="center">
            <CircularProgress size={26} />
          </MDBox>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <MDTypography variant="button" fontWeight="bold">
                Últimos 3 contactos
              </MDTypography>

              <Divider sx={{ my: 1 }} />

              {!historico.length ? (
                <MDTypography variant="caption" color="text">
                  Não existem contactos registados.
                </MDTypography>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={1}>
                  {historico.map((h, idx) => (
                    <MDBox key={`${h?.id || "hist"}_${idx}`}>
                      <MDTypography variant="caption" display="block">
                        <strong>Data:</strong> {fmtDateTime(h?.datacontacto)}
                      </MDTypography>

                      <MDTypography variant="caption" display="block">
                        <strong>Utilizador:</strong> {h?.utilizador || "-"}
                      </MDTypography>

                      <MDTypography variant="caption" display="block">
                        <strong>Realizado:</strong>{" "}
                        {Number(h?.contacto_realizado) === 1 ? "Sim" : "Não"}
                      </MDTypography>

                      <MDTypography variant="caption" display="block">
                        <strong>Obs.:</strong> {h?.observacoes || "-"}
                      </MDTypography>

                      {idx < historico.length - 1 ? <Divider sx={{ mt: 1, mb: 0.5 }} /> : null}
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <MDTypography variant="button" fontWeight="bold">
                Últimas 5 faturas
              </MDTypography>

              <Divider sx={{ my: 1 }} />

              <AgruparLinhasPorFatura faturas={faturas} linhas={linhas} />
            </Paper>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose} color="light">
          Fechar
        </MDButton>
      </DialogActions>

      <Dialog
        open={openInativarAviso}
        onClose={() => setOpenInativarAviso(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Inativar aviso</DialogTitle>

        <DialogContent dividers>
          <MDTypography variant="button" color="text">
            Pretende deixar de mostrar este cliente nesta lista de débitos sem compras há mais de 30
            dias?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <MDButton color="light" onClick={() => setOpenInativarAviso(false)}>
            Cancelar
          </MDButton>

          <MDButton
            color="error"
            onClick={handleConfirmarInativarAviso}
            disabled={savingInativarAviso}
          >
            {savingInativarAviso ? "A gravar..." : "Inativar aviso"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
